"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { GitmapColors } from "./Gitmap";

const COLS = 53;
const ROWS = 7;

interface OverlayProps {
    cellSize: number;
    cellGap: number;
    colors: GitmapColors;
    onExit: () => void;
}

function Cell({
    c,
    r,
    cellSize,
    cellGap,
    color,
    leftOffset,
    topOffset,
}: {
    c: number;
    r: number;
    cellSize: number;
    cellGap: number;
    color: string;
    leftOffset: number;
    topOffset: number;
}) {
    return (
        <div
            className="absolute rounded-[2px] transition-[background-color] duration-150"
            style={{
                left: leftOffset + c * (cellSize + cellGap),
                top: topOffset + r * (cellSize + cellGap),
                width: cellSize,
                height: cellSize,
                backgroundColor: color,
            }}
        />
    );
}

function gridSize(cellSize: number, cellGap: number) {
    return {
        width: COLS * cellSize + (COLS - 1) * cellGap,
        height: ROWS * cellSize + (ROWS - 1) * cellGap,
    };
}

export function SnakeOverlay({ cellSize, cellGap, colors, onExit }: OverlayProps) {
    const [snake, setSnake] = useState<{ c: number; r: number }[]>([
        { c: 12, r: 3 },
        { c: 11, r: 3 },
        { c: 10, r: 3 },
    ]);
    const [dir, setDir] = useState<{ dx: number; dy: number }>({ dx: 1, dy: 0 });
    const dirQueueRef = useRef<{ dx: number; dy: number } | null>(null);
    const [food, setFood] = useState<{ c: number; r: number }>({ c: 30, r: 3 });
    const [dead, setDead] = useState(false);
    const [score, setScore] = useState(0);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                onExit();
                return;
            }
            const cur = dir;
            const set = (dx: number, dy: number) => {
                if (cur.dx === -dx && cur.dy === -dy) return;
                dirQueueRef.current = { dx, dy };
            };
            if (e.key === "ArrowUp" || e.key === "w") {
                e.preventDefault();
                set(0, -1);
            } else if (e.key === "ArrowDown" || e.key === "s") {
                e.preventDefault();
                set(0, 1);
            } else if (e.key === "ArrowLeft" || e.key === "a") {
                e.preventDefault();
                set(-1, 0);
            } else if (e.key === "ArrowRight" || e.key === "d") {
                e.preventDefault();
                set(1, 0);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [dir, onExit]);

    useEffect(() => {
        if (dead) {
            const t = setTimeout(onExit, 1400);
            return () => clearTimeout(t);
        }
        const id = setInterval(() => {
            setSnake((prev) => {
                const queued = dirQueueRef.current;
                let curDir = dir;
                if (queued) {
                    curDir = queued;
                    dirQueueRef.current = null;
                    setDir(queued);
                }
                const head = prev[0];
                const nh = { c: head.c + curDir.dx, r: head.r + curDir.dy };
                if (nh.c < 0 || nh.c >= COLS || nh.r < 0 || nh.r >= ROWS) {
                    setDead(true);
                    return prev;
                }
                if (prev.some((s) => s.c === nh.c && s.r === nh.r)) {
                    setDead(true);
                    return prev;
                }
                const next = [nh, ...prev];
                if (nh.c === food.c && nh.r === food.r) {
                    setScore((s) => s + 1);
                    let f: { c: number; r: number };
                    let attempts = 0;
                    do {
                        f = {
                            c: Math.floor(Math.random() * COLS),
                            r: Math.floor(Math.random() * ROWS),
                        };
                        attempts++;
                    } while (
                        next.some((s) => s.c === f.c && s.r === f.r) &&
                        attempts < 200
                    );
                    setFood(f);
                } else {
                    next.pop();
                }
                return next;
            });
        }, 110);
        return () => clearInterval(id);
    }, [dir, food, dead, onExit]);

    const headColor = dead ? "#dc2626" : colors.level4;
    const bodyColor = colors.level3;
    const foodColor = colors.level1;
    const { width, height } = gridSize(cellSize, cellGap);
    const leftOffset = 28;
    const padding = Math.max(3, Math.floor(cellGap) + 1);
    const cellsTop = padding;
    const statusTop = height + padding * 2 + 6;
    const containerHeight = statusTop + 16;

    return (
        <div
            className="relative"
            style={{
                width: width + leftOffset,
                height: containerHeight,
            }}
        >
            <div
                className={cn(
                    "absolute rounded-md border-2 border-dashed transition-colors",
                    dead ? "border-red-500/70" : "border-foreground/45",
                )}
                style={{
                    left: leftOffset - padding,
                    top: 0,
                    width: width + padding * 2,
                    height: height + padding * 2,
                }}
            />
            {snake.map((s, i) => (
                <Cell
                    key={`s-${i}`}
                    c={s.c}
                    r={s.r}
                    cellSize={cellSize}
                    cellGap={cellGap}
                    color={i === 0 ? headColor : bodyColor}
                    leftOffset={leftOffset}
                    topOffset={cellsTop}
                />
            ))}
            <Cell
                c={food.c}
                r={food.r}
                cellSize={cellSize}
                cellGap={cellGap}
                color={foodColor}
                leftOffset={leftOffset}
                topOffset={cellsTop}
            />
            <div
                className={cn(
                    "absolute font-mono text-[11px] tracking-wide",
                    dead ? "text-red-500" : "text-muted-foreground",
                )}
                style={{ top: statusTop, left: leftOffset - padding }}
            >
                {dead ? `game over · ${score}` : `snake · ${score} · esc`}
            </div>
        </div>
    );
}

interface Drop {
    col: number;
    head: number;
    length: number;
    speed: number;
}

export function MatrixOverlay({ cellSize, cellGap, colors, onExit }: OverlayProps) {
    const [drops, setDrops] = useState<Drop[]>(() =>
        Array.from({ length: COLS }, (_, col) => ({
            col,
            head: -Math.floor(Math.random() * ROWS * 3),
            length: 2 + Math.floor(Math.random() * 4),
            speed: 0.4 + Math.random() * 0.7,
        })),
    );

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                onExit();
            }
        };
        window.addEventListener("keydown", onKey);
        const exit = setTimeout(onExit, 9000);
        const id = setInterval(() => {
            setDrops((prev) =>
                prev.map((d) => {
                    let head = d.head + d.speed;
                    if (head - d.length > ROWS + 2) {
                        head = -Math.floor(Math.random() * ROWS * 2);
                    }
                    return { ...d, head };
                }),
            );
        }, 90);
        return () => {
            window.removeEventListener("keydown", onKey);
            clearTimeout(exit);
            clearInterval(id);
        };
    }, [onExit]);

    const { width, height } = gridSize(cellSize, cellGap);
    const leftOffset = 28;
    const topOffset = 0;
    const statusTop = height + 6;
    const containerHeight = statusTop + 16;

    const cellsToRender: { c: number; r: number; color: string; key: string }[] = [];
    for (const d of drops) {
        const headInt = Math.floor(d.head);
        for (let i = 0; i < d.length; i++) {
            const r = headInt - i;
            if (r < 0 || r >= ROWS) continue;
            const ratio = i / Math.max(1, d.length - 1);
            const color =
                i === 0
                    ? colors.level4
                    : ratio < 0.4
                      ? colors.level3
                      : ratio < 0.75
                        ? colors.level2
                        : colors.level1;
            cellsToRender.push({ c: d.col, r, color, key: `${d.col}-${r}` });
        }
    }

    return (
        <div
            className="relative"
            style={{
                width: width + leftOffset,
                height: containerHeight,
            }}
        >
            {cellsToRender.map((c) => (
                <Cell
                    key={c.key}
                    c={c.c}
                    r={c.r}
                    cellSize={cellSize}
                    cellGap={cellGap}
                    color={c.color}
                    leftOffset={leftOffset}
                    topOffset={topOffset}
                />
            ))}
            <div
                className="absolute font-mono text-[11px] tracking-wide text-muted-foreground"
                style={{ top: statusTop, left: leftOffset }}
            >
                wake up, neo… · esc
            </div>
        </div>
    );
}
